"use client";

import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP);
export default function Agriculturehero() {
  const container = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".hero-eyebrow", {
        y: 24,
        filter: "blur(8px)"
      });
      gsap.set(".hero-headline-line", {
        y: 40,
        filter: "blur(10px)"
      });
      gsap.set(".hero-ring", {
        rotate: 0,
        transformOrigin: "50% 50%"
      });
      const tl = gsap.timeline({
        defaults: {
          ease: "power3.out"
        }
      });
      tl.to(".hero-eyebrow", {
        opacity: 1,
        y: 0,
        filter: "blur(0px)",
        duration: 0.72
      }).to(".hero-headline-line", {
        opacity: 1,
        y: 0,
        filter: "blur(0px)",
        duration: 0.9,
        stagger: 0.09
      }, "-=0.5");
      gsap.to(".hero-ring", {
        rotate: 360,
        duration: 120,
        ease: "none",
        repeat: -1
      });
      gsap.to(".hero-aurora", {
        yPercent: 8,
        ease: "none",
        scrollTrigger: {
          trigger: container.current,
          start: "top top",
          end: "bottom top",
          scrub: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".hero-eyebrow", ".hero-headline-line"], {
        opacity: 1,
        y: 0,
        filter: "blur(0px)"
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="31350" id="agriculturehero" data-nav="dark" ref={container} className="relative isolate min-h-[92vh] w-full overflow-hidden bg-background text-foreground" style={{
    backgroundColor: "hsl(215 80% 20%)"
  }}>
      {}
      <div className="absolute inset-0 -z-20">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/industrial_spraying_system.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/industrial_spraying_system.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/industrial_spraying_system.png-wvc-sizes" alt="Sistema de pulverização industrial de alta precisão em ambiente fabril" className="hero-aurora h-full w-full object-cover opacity-90" loading="eager" decoding="async" />
        {}
        <div className="absolute inset-0" style={{
        background: "radial-gradient(120% 90% at 12% 88%, hsla(215, 90%, 45%, 0.35) 0%, hsla(215, 80%, 20%, 0.35) 40%, hsl(215 80% 20%) 82%)"
      }} />
        <div className="absolute inset-0" style={{
        background: "linear-gradient(210deg, hsla(215, 80%, 20%, 0.9) 0%, hsla(215, 80%, 20%, 0.15) 45%, hsla(215, 80%, 20%, 0.6) 100%)"
      }} />
      </div>

      {}
      <div className="pointer-events-none absolute -bottom-[20%] -left-[10%] -z-10 h-[1200px] w-[1200px]">
        <div className="hero-ring absolute inset-0" style={{
        background: "radial-gradient(closest-side, hsla(215, 90%, 45%, 0.30) 0%, hsla(215, 90%, 45%, 0.12) 35%, transparent 70%)"
      }} />
        <div className="absolute inset-[18%] rounded-lg opacity-40" style={{
        border: "1px solid hsla(210, 10%, 85%, 0.18)"
      }} />
        <div className="absolute inset-[30%] rounded-lg opacity-30" style={{
        border: "1px solid hsla(210, 10%, 92%, 0.15)"
      }} />
      </div>

      {}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{
      backgroundColor: "hsla(210, 10%, 85%, 0.14)"
    }} />

      {}
      <div className="relative z-10 mx-auto flex min-h-[92vh] w-full max-w-[1360px] flex-col justify-end px-6 pb-20 pt-32 sm:px-8 md:pb-28 md:pt-40 lg:px-14 lg:pb-32 lg:pt-48">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-end lg:gap-8">
          {}
          <div className="lg:col-span-7">
            <div className="hero-eyebrow flex items-center gap-3" style={{
            opacity: 0
          }}>
              <span className="h-px w-6" style={{
              backgroundColor: "hsl(215 90% 45%)"
            }} aria-hidden="true" />
              <span className="font-default text-xs font-medium uppercase" style={{
              letterSpacing: "0.22em",
              color: "hsl(210 10% 92%)"
            }}>
                Eurocontrol Engenharia
              </span>
            </div>

            <h1 className="mt-8 font-default text-[hsl(210_20%_98%)]" style={{
            fontSize: "clamp(3.25rem, 7.2vw, 7.5rem)",
            fontWeight: 450,
            letterSpacing: "-0.035em",
            lineHeight: 0.96
          }}>
              <span className="hero-headline-line block" style={{
              opacity: 0
            }}>
                Especialistas em
              </span>
              <span className="hero-headline-line block" style={{
              opacity: 0
            }}>
                Pulverização.
              </span>
            </h1>
          </div>

          {}
          <div className="lg:col-span-5 lg:-mt-32">
            <motion.div initial={prefersReducedMotion ? {
            opacity: 1,
            y: 0,
            scale: 1,
            filter: "blur(0px)"
          } : {
            opacity: 0,
            y: 40,
            scale: 0.98,
            filter: "blur(12px)"
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1,
            filter: "blur(0px)"
          }} transition={{
            duration: 0.9,
            delay: prefersReducedMotion ? 0 : 1.05,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative rounded-3xl p-8 sm:p-10" style={{
            backgroundColor: "hsla(210, 10%, 92%, 0.08)",
            border: "1px solid hsla(210, 10%, 85%, 0.14)",
            backdropFilter: "blur(20px) saturate(140%)",
            WebkitBackdropFilter: "blur(20px) saturate(140%)",
            boxShadow: "0 40px 80px -40px hsla(215, 90%, 45%, 0.35), inset 0 1px 0 hsla(210, 10%, 85%, 0.08)"
          }}>
              <div className="flex items-center gap-3">
                <span className="h-px w-6" style={{
                backgroundColor: "hsl(215 90% 45%)"
              }} aria-hidden="true" />
                <span className="font-default text-xs font-medium uppercase" style={{
                letterSpacing: "0.22em",
                color: "hsl(210 10% 92%)"
              }}>
                  Desde 1937
                </span>
              </div>

              <p className="mt-6 font-default" style={{
              fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
              fontWeight: 350,
              lineHeight: 1.55,
              letterSpacing: "-0.005em",
              color: "hsl(210 20% 98%)"
            }}>
                Representantes exclusivos da Spraying Systems Co. em Portugal desde 1993, oferecendo soluções de engenharia industrial de alta performance.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                <Button asChild className="group h-auto rounded-lg py-3 pl-6 pr-2 text-sm font-medium transition-all" style={{
                backgroundColor: "hsl(215 90% 45%)",
                color: "hsl(210 20% 98%)"
              }}>
                  <Link to="/ContactsPage" className="inline-flex items-center gap-3">
                    <span>Consulte-nos</span>
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg transition-transform duration-500 group-hover:-rotate-45" style={{
                    backgroundColor: "hsl(210 20% 98%)"
                  }}>
                      <ArrowUpRight className="h-4 w-4" style={{
                      color: "hsl(215 90% 45%)"
                    }} strokeWidth={1.5} />
                    </span>
                  </Link>
                </Button>

                <Button asChild variant="ghost" className="group h-auto rounded-lg px-6 py-3 text-sm font-medium hover:bg-transparent" style={{
                backgroundColor: "hsla(210, 10%, 92%, 0.06)",
                border: "1px solid hsla(210, 10%, 85%, 0.14)",
                color: "hsl(210 20% 98%)"
              }}>
                  <Link to="/ProductsPage" className="inline-flex items-center gap-2">
                    <span>Ver Produtos</span>
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-0.5" strokeWidth={1.5} />
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>

        {}
        <div className="mt-16 flex items-center justify-between border-t pt-6" style={{
        borderColor: "hsla(210, 10%, 85%, 0.10)"
      }}>
          <span className="font-default text-xs font-medium uppercase" style={{
          letterSpacing: "0.22em",
          color: "hsl(210 10% 92%)"
        }}>
            Suporte Técnico Especializado
          </span>
          <span className="hidden font-default text-xs font-medium uppercase sm:inline" style={{
          letterSpacing: "0.22em",
          color: "hsl(210 10% 92%)"
        }}>
            Role para continuar
          </span>
        </div>
      </div>
    </section>;
}